import { useState } from 'react';
import { AlertTriangle, Loader2, ShieldAlert } from 'lucide-react';
import { useThemeClasses } from '@/hooks/useTheme';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCOUNTABILITY_REASONS = [
  { id: 'disrespectful', label: 'Being Disrespectful', description: 'Disrespecting other members or the culture' },
  { id: 'clout_chasing', label: 'Clout Chasing', description: 'Using the Cookout for clout without giving back' },
  { id: 'bad_faith', label: 'Bad Faith / Trolling', description: 'Stirring up drama or acting in bad faith' },
  { id: 'not_keeping_it_real', label: 'Not Keeping It Real', description: 'Being fake, dishonest, or two-faced' },
  { id: 'other', label: 'Something Else', description: 'Another reason not listed above' },
];

export const AccountabilityModal = ({
  open,
  onOpenChange,
  targetUserId,
  targetPostId,
  targetName,
}) => {
  const { isDark, textClass, textMutedClass, borderClass } = useThemeClasses();
  const [selectedReason, setSelectedReason] = useState(null);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedReason) {
      toast.error('Pick a reason first');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/vouch/accountability/vote`, {
        target_user_id: targetUserId,
        target_post_id: targetPostId,
        reason: selectedReason,
        details: details.trim() || null,
      }, { withCredentials: true });

      const data = res.data;
      if (data.removed) {
        toast.success('Community has spoken. Member removed from the Cookout.');
      } else {
        toast(`Your voice has been heard. ${data.vote_count}/${data.threshold} needed.`, { icon: '🗣️' });
      }

      onOpenChange(false);
      setSelectedReason(null);
      setDetails('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit vote';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setSelectedReason(null);
    setDetails('');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={cn(
        "sm:max-w-[450px] p-0 overflow-hidden",
        isDark ? "bg-black border-white/10" : "bg-white border-gray-200"
      )}>
        <DialogHeader className={cn("p-4 border-b", borderClass)}>
          <DialogTitle className={cn("font-display text-sm tracking-widest uppercase flex items-center gap-2", textClass)}>
            <ShieldAlert className="h-4 w-4" />
            Come Get Your Mans
          </DialogTitle>
          <DialogDescription className={textMutedClass}>
            {targetName && `Calling out: ${targetName}`}
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 space-y-4">
          {/* Warning */}
          <div className={cn(
            "flex items-start gap-3 p-3 rounded-lg text-sm",
            isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-700"
          )}>
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>This is a community accountability vote. If enough members speak up, this person will be removed from the Cookout and their voucher loses a plate.</p>
          </div>

          {/* Reason Selection */}
          <div>
            <p className={cn("text-xs uppercase tracking-wider mb-3", textMutedClass)}>
              What happened?
            </p>
            <div className="space-y-2">
              {ACCOUNTABILITY_REASONS.map((reason) => (
                <button
                  key={reason.id}
                  onClick={() => setSelectedReason(reason.id)}
                  className={cn(
                    "w-full p-3 text-left border transition-all",
                    selectedReason === reason.id
                      ? isDark
                        ? "border-red-500 bg-red-500/10"
                        : "border-red-600 bg-red-50"
                      : cn(borderClass, isDark ? "hover:border-white/30" : "hover:border-gray-400")
                  )}
                >
                  <p className={cn("font-medium text-sm", textClass)}>{reason.label}</p>
                  <p className={cn("text-xs mt-0.5", textMutedClass)}>{reason.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Additional Details */}
          {selectedReason && (
            <div>
              <p className={cn("text-xs uppercase tracking-wider mb-2", textMutedClass)}>
                Details (optional)
              </p>
              <Textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="What specifically went down..."
                className={cn(
                  "min-h-[80px] resize-none bg-transparent text-sm",
                  isDark ? "border-white/20 focus:border-white" : "border-gray-300 focus:border-gray-900"
                )}
                maxLength={500}
              />
              <p className={cn("text-[10px] mt-1 text-right", textMutedClass)}>
                {details.length}/500
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={handleClose}
              className="flex-1"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!selectedReason || submitting}
              className={cn(
                "flex-1",
                isDark ? "bg-red-500 hover:bg-red-600 text-white" : "bg-red-600 hover:bg-red-700 text-white"
              )}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Speak Up'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountabilityModal;
